import React from 'react'
import { Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { Button, Input, Form, Checkbox } from 'components/Forms'
import { closeModal } from 'store/actions/modals'
import { useProgress } from 'components/Hooks'
import { ScrollableUserList } from 'components/UI'
import { refreshCounter } from 'store/actions/refresh'
import { Alert, Card } from 'ui'

import { t } from 'translations'

const UsersArchiveModal = ({ isArchiving, userIds }) => {
  const dispatch = useDispatch()

  const [Progress, progressValues, setProgress] = useProgress()

  const affectedResources = ['shifts', 'templateEvents', 'teams', 'projectOwners']

  const submit = async () => {
    let currentUser = 0
    let failedUsers = []
    for (const userId of userIds) {
      setProgress({ userId, width: Math.trunc((++currentUser * 100) / userIds.length), hasFailedUsers: failedUsers.length })
      const { success } = await Api.put(`/users/archive/${isArchiving ? 'archive' : 'unarchive'}`, { id: userId })
      if (!success) failedUsers.push(userId)
    }
    dispatch(refreshCounter('users'))
    setProgress({ width: 100, failedUsers, done: true })
  }

  if (!isArchiving)
    return (
      <BaseModal hideButtons title="manager.users.unarchiveUsersModal">
        <ScrollableUserList usersIds={userIds} />

        <div className="mt-3">
          <Button type="button" text="actions.CONFIRM" className="btn btn-danger mr-2" disabled={!!progressValues} onClick={submit} />
          <Button
            type="button"
            text="actions.CLOSE"
            className="btn btn-outline-secondary"
            onClick={() => dispatch(closeModal())}
            disabled={!!progressValues && !progressValues.done}
          />
        </div>
        <Progress />
      </BaseModal>
    )

  return (
    <BaseModal hideButtons title="manager.users.archiveUsersModal">
      <div className="mb-3"></div>
      <ScrollableUserList usersIds={userIds} />
      <Formik
        enableReinitialize={true}
        validationSchema={Yup.object().shape({
          confirm: Yup.string()
            .test(
              'ARCHIVE',
              t('manager.users.archiveUsersModal.BAD_MATCH'),
              value => value?.toLowerCase() === t('manager.users.archiveUsersModal.CONFIRM.WORD')
            )
            .required('manager.users.archiveUsersModal.FIELD_IS_REQUIRED'),
          confirmAmount: Yup.mixed()
            .oneOf([userIds.length], t('manager.users.archiveUsersModal.CONFIRMAMOUNT.BAD_CONFIRMAMOUNT_CONFIRMATION', { count: userIds.length }))
            .required('manager.users.archiveUsersModal.CONFIRMAMOUNT.CONFIRMAMOUNT_IS_REQUIRED')
        })}
        initialValues={{ confirm: '', confirmAmount: '' }}
        onSubmit={submit}
      >
        {({ values, status, isSubmitting, setStatus }) => {
          const canArchive =
            affectedResources.every(resource => values[resource]) &&
            values.confirmAmount === userIds.length &&
            values.confirm.toLowerCase() === t('manager.users.archiveUsersModal.CONFIRM.WORD')

          return (
            <Form className="mt-2">
              <Card className="bg-light my-2">
                <Card.Body>
                  <Trans t={t} i18nKey={'manager.users.archiveUsersModal.WILL_BE_IMPACTED'} components={{ h6: <h6 /> }} />
                  <ul className="mt-3">
                    {affectedResources.map(resourceName => {
                      return (
                        <Checkbox
                          key={`${resourceName}`}
                          name={`${resourceName}`}
                          noDescription
                          label={' '}
                          required
                          customLabelElement={
                            <Trans t={t} i18nKey={`manager.users.archiveUsersModal.${resourceName.toUpperCase()}`}>
                              <b></b>
                            </Trans>
                          }
                        />
                      )
                    })}
                  </ul>
                </Card.Body>
              </Card>

              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <Input module="manager.users.archiveUsersModal" name="confirm" required />
              <Input module="manager.users.archiveUsersModal" type="number" name="confirmAmount" required />
              <Button className="btn btn-danger mr-2" type="submit" isSubmitting={isSubmitting} disabled={!canArchive}>
                {t(`manager.users.archiveUsersModal.ARCHIVE_SUBMIT`, {
                  count: userIds.length
                })}
              </Button>
              <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                {t(`actions.CLOSE`)}
              </Button>
              <Progress />
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default UsersArchiveModal
